import { useActiveScopeCapabilities } from "@/hooks";
import { KnownCapability, ScopeLevel } from "@/types/capability.types";
import { hasCapability } from "@/utils/capability";

// The news owner scope (league > organization > platform, see useNewsOwner)
// matches the active scope level 1:1, so the write keys are looked up by the
// active level and checked against that level's own capability set. Keys are
// scope-exact on purpose — the backend enforces `<LEVEL>_NEWS:*` at the owner
// node without implied-capability expansion, so accepting ancestor-level keys
// here would light up actions the API then denies.
const NEWS_READ_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_NEWS:READ",
  organization: "ORG_NEWS:READ",
  league: "LEAGUE_NEWS:READ",
};

const NEWS_CREATE_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_NEWS:CREATE",
  organization: "ORG_NEWS:CREATE",
  league: "LEAGUE_NEWS:CREATE",
};

const NEWS_WRITE_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_NEWS:WRITE",
  organization: "ORG_NEWS:WRITE",
  league: "LEAGUE_NEWS:WRITE",
};

const NEWS_DELETE_KEYS: Record<ScopeLevel, KnownCapability> = {
  platform: "PLATFORM_NEWS:DELETE",
  organization: "ORG_NEWS:DELETE",
  league: "LEAGUE_NEWS:DELETE",
};

/**
 * Permissions for the news surface at the active scope. `canSeeNews`
 * (`<LEVEL>_NEWS:READ`) gates the news list and detail/edit pages.
 * `canCreateNews` / `canUpdateNews` / `canDeleteNews` gate their corresponding
 * mutations with the scope-exact `<LEVEL>_NEWS:*` key.
 */
export const useNewsPermissions = () => {
  const { level, capabilities } = useActiveScopeCapabilities();

  return {
    canSeeNews: hasCapability(capabilities, NEWS_READ_KEYS[level]),
    canCreateNews: hasCapability(capabilities, NEWS_CREATE_KEYS[level]),
    canUpdateNews: hasCapability(capabilities, NEWS_WRITE_KEYS[level]),
    canDeleteNews: hasCapability(capabilities, NEWS_DELETE_KEYS[level]),
  };
};
